import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  useBillingControllerPaymentExceptionsQuery,
  useBillingControllerResolvePaymentExceptionMutation,
} from '@shared/services/apiService';

import { billingErrorMessage } from '../model/billingError';

export const BillingPaymentExceptions = ({ companyId }: { companyId: string }) => {
  const query = useBillingControllerPaymentExceptionsQuery({ companyId });
  const [resolve, operation] = useBillingControllerResolvePaymentExceptionMutation();
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [supportReference, setSupportReference] = useState('');
  const [error, setError] = useState('');
  const keys = useRef(new Map<string, string>());
  const submit = async () => {
    if (!selected || !reason.trim() || !supportReference.trim()) return;
    const semantic = JSON.stringify([companyId, selected, reason.trim(), supportReference.trim()]);
    if (!keys.current.has(semantic)) keys.current.set(semantic, crypto.randomUUID());
    setError('');
    try {
      await resolve({
        companyId,
        exceptionId: selected,
        resolveBillingPaymentExceptionDto: {
          requestKey: keys.current.get(semantic)!,
          reason: reason.trim(),
          supportReference: supportReference.trim(),
        },
      }).unwrap();
      keys.current.delete(semantic);
      setSelected(null);
      setReason('');
      setSupportReference('');
      void query.refetch();
    } catch (value) {
      setError(billingErrorMessage(value));
      void query.refetch();
    }
  };
  const open = query.data?.exceptions.filter((e) => !e.resolvedAt) ?? [];
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Payment reviews</Typography>
      <Typography color="text.secondary">
        Refunds and disputes block new calls until an operator reviews each case. Active calls
        finish. Releasing a payment block preserves credits and other access checks or pauses.
      </Typography>
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
          Payment reviews could not be loaded.
        </Alert>
      )}
      {error && !selected && <Alert severity="error">{error}</Alert>}
      {query.data && !open.length && <Typography>No unresolved payment reviews.</Typography>}
      {open.map((entry) => (
        <Alert severity="warning" key={entry.id}>
          <Stack spacing={1}>
            <Typography>
              {entry.kind === 'refund' ? 'Refund' : 'Dispute'} ·{' '}
              {new Date(entry.createdAt).toLocaleString()}
            </Typography>
            <Typography variant="body2">Case: {entry.id}</Typography>
            <Button
              variant="outlined"
              disabled={operation.isLoading}
              onClick={() => {
                setSelected(entry.id);
                setReason('');
                setSupportReference('');
                setError('');
              }}>
              Review {entry.kind}
            </Button>
          </Stack>
        </Alert>
      ))}
      {query.data?.hasMore && (
        <Alert severity="info">
          Only the first 100 cases are shown; unresolved cases appear first.
        </Alert>
      )}
      <Dialog
        open={!!selected}
        onClose={() => {
          if (!operation.isLoading) setSelected(null);
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Review payment block</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography>
              Allow new calls for this case after manual review. Other payment blocks, operational
              pauses, expired periods and credit limits still apply. No credits or monetary refunds
              are changed.
            </Typography>
            <Typography variant="body2">Case: {selected}</Typography>
            <TextField
              label="Payment review reason"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={operation.isLoading}
              inputProps={{ maxLength: 2000 }}
            />
            <TextField
              label="Review evidence reference"
              value={supportReference}
              onChange={(e) => setSupportReference(e.target.value)}
              disabled={operation.isLoading}
              inputProps={{ maxLength: 2000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={operation.isLoading}>
            Keep blocked
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={operation.isLoading || !reason.trim() || !supportReference.trim()}>
            Release this payment block
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
